/**
 * VoucherTemplateEditor — Main orchestrator
 *
 * Architecture (Feb 2026):
 *   VoucherTemplateEditor.js  — state, mouse handlers, keyboard shortcuts, layout
 *   editor/editorConstants.js — DYNAMIC_FIELDS, SAMPLE_DATA, ELEMENT_DEFAULTS, uid, elLabel, elIcon
 *   editor/presetTemplates.js — PRESET_TEMPLATES (4 professional layouts)
 *   editor/useEditorHistory.js — undo/redo history hook
 *   editor/EditorToolbar.js   — top toolbar (save, undo, presets, reset, preview)
 *   editor/EditorLeftPanel.js — element palette, dynamic fields, background, layers
 *   editor/EditorPropertiesPanel.js — selected element properties
 *   editor/CanvasElement.js   — individual element renderer on canvas
 *   editor/EditorDialogs.js   — presets gallery + reset confirmation
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

import { A4_RATIO, uid, ELEMENT_DEFAULTS } from "./editor/editorConstants";
import { PRESET_TEMPLATES } from "./editor/presetTemplates";
import { useEditorHistory } from "./editor/useEditorHistory";
import { EditorToolbar } from "./editor/EditorToolbar";
import { EditorLeftPanel } from "./editor/EditorLeftPanel";
import { EditorPropertiesPanel } from "./editor/EditorPropertiesPanel";
import { CanvasElement } from "./editor/CanvasElement";
import { PresetsGallery, ResetConfirmDialog } from "./editor/EditorDialogs";

const API = process.env.REACT_APP_BACKEND_URL;

function createDefaultElements() {
  const preset = PRESET_TEMPLATES.find(p => p.id === "lavado_premium");
  return preset ? preset.layout.elements.map(e => ({ ...e, id: uid() })) : [];
}

export default function VoucherTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const canvasRef = useRef(null);

  const [template, setTemplate] = useState(null);
  const [elements, setElements] = useState([]);
  const [bgColor, setBgColor] = useState("#f1f5f9");
  const [bgImage, setBgImage] = useState("");
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ w: 500, h: 500 * A4_RATIO });

  const { history, pushHistory, undo } = useEditorHistory(elements, bgColor, bgImage);

  // Load template
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axiosAuth().get(`/vouchers/templates/${id}`);
        setTemplate(r.data);
        const layout = r.data.layout;
        if (layout && layout.elements && layout.elements.length > 0) {
          setElements(layout.elements);
          setBgColor(layout.pageBackground || "#f1f5f9");
          setBgImage(layout.backgroundImage || "");
        } else {
          setElements(createDefaultElements());
        }
      } catch {
        toast.error("Error al cargar plantilla");
        navigate("/admin/atenciones/plantillas");
      }
    };
    load();
  }, [id, axiosAuth, navigate]);

  // Canvas sizing
  useEffect(() => {
    const resize = () => {
      const maxW = Math.min(window.innerWidth - 470, 620);
      const maxH = window.innerHeight - 140;
      let w = maxW;
      let h = w * A4_RATIO;
      if (h > maxH) { h = maxH; w = h / A4_RATIO; }
      setCanvasSize({ w, h });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSelected(); }
      if (e.ctrlKey && e.key === "z") { e.preventDefault(); handleUndo(); }
      if (e.ctrlKey && e.key === "d") { e.preventDefault(); duplicateSelected(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Save layout
  const save = async () => {
    setSaving(true);
    try {
      const layout = { pageBackground: bgColor, backgroundImage: bgImage, elements };
      await axiosAuth().put(`/vouchers/templates/${id}`, { layout });
      setSavedFeedback(true);
      setTimeout(() => setSavedFeedback(false), 2000);
      toast.success("Plantilla guardada correctamente");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  // Upload background image
  const uploadBgImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pushHistory();
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await axiosAuth().post("/vouchers/templates/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      setBgImage(r.data.url);
      toast.success("Imagen de fondo subida");
    } catch { toast.error("Error al subir imagen"); }
  };

  // Upload custom image element
  const uploadCustomImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pushHistory();
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await axiosAuth().post("/vouchers/templates/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const maxZ = elements.length > 0 ? Math.max(...elements.map(e => e.zIndex || 0)) + 1 : 1;
      const newEl = { id: uid(), type: "image", source: r.data.url, x: 30, y: 30, width: 20, height: 14, zIndex: maxZ, opacity: 1 };
      setElements(prev => [...prev, newEl]);
      setSelected(newEl.id);
      toast.success("Imagen subida y anadida");
    } catch { toast.error("Error al subir imagen"); }
  };

  // Element operations
  const addElement = (type, extra = {}) => {
    pushHistory();
    const maxZ = elements.length > 0 ? Math.max(...elements.map(e => e.zIndex || 0)) + 1 : 1;
    const el = { id: uid(), ...ELEMENT_DEFAULTS[type], zIndex: maxZ, ...extra };
    setElements(prev => [...prev, el]);
    setSelected(el.id);
  };

  const deleteSelected = () => {
    if (!selected) return;
    const el = elements.find(e => e.id === selected);
    if (el?.locked) { toast.error("Elemento bloqueado"); return; }
    pushHistory();
    setElements(prev => prev.filter(e => e.id !== selected));
    setSelected(null);
  };

  const duplicateSelected = () => {
    if (!selected) return;
    pushHistory();
    const el = elements.find(e => e.id === selected);
    if (!el) return;
    const newEl = { ...el, id: uid(), x: Math.min(el.x + 2, 95), y: Math.min(el.y + 2, 95), locked: false };
    setElements(prev => [...prev, newEl]);
    setSelected(newEl.id);
  };

  const toggleLock = () => {
    if (!selected) return;
    setElements(prev => prev.map(e => e.id === selected ? { ...e, locked: !e.locked } : e));
  };

  const bringToFront = () => {
    if (!selected) return;
    pushHistory();
    const maxZ = Math.max(...elements.map(e => e.zIndex || 0)) + 1;
    setElements(prev => prev.map(e => e.id === selected ? { ...e, zIndex: maxZ } : e));
  };

  const sendToBack = () => {
    if (!selected) return;
    pushHistory();
    setElements(prev => prev.map(e => e.id === selected ? { ...e, zIndex: 0 } : e));
  };

  const centerH = () => {
    if (!selected) return;
    pushHistory();
    setElements(prev => prev.map(e => e.id === selected ? { ...e, x: Math.round((100 - e.width) / 2 * 10) / 10 } : e));
  };

  const updateEl = (prop, value) => {
    if (!selected) return;
    setElements(prev => prev.map(e => e.id === selected ? { ...e, [prop]: value } : e));
  };

  const handleUndo = () => undo(setElements, setBgColor, setBgImage, setSelected);

  const loadPreset = (preset) => {
    pushHistory();
    setElements(preset.layout.elements.map(e => ({ ...e, id: uid() })));
    setBgColor(preset.layout.pageBackground || "#f1f5f9");
    setBgImage(preset.layout.backgroundImage || "");
    setSelected(null);
    setShowPresets(false);
    toast.success(`Plantilla "${preset.name}" cargada`);
  };

  const resetToDefault = () => {
    pushHistory();
    setElements(createDefaultElements());
    setBgColor("#f1f5f9");
    setBgImage("");
    setSelected(null);
    setShowResetConfirm(false);
    toast.success("Layout restablecido");
  };

  // Mouse handlers
  const getCanvasCoords = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 };
  };

  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current || e.target.dataset.canvas === "true") setSelected(null);
  };

  const handleElMouseDown = (e, elId) => {
    e.stopPropagation(); e.preventDefault();
    const el = elements.find(x => x.id === elId);
    if (el?.locked) { setSelected(elId); return; }
    pushHistory();
    setSelected(elId);
    const coords = getCanvasCoords(e);
    setDragging({ id: elId, offsetX: coords.x - el.x, offsetY: coords.y - el.y });
  };

  const handleResizeMouseDown = (e, elId, handle) => {
    e.stopPropagation(); e.preventDefault();
    const el = elements.find(x => x.id === elId);
    if (el?.locked) return;
    pushHistory();
    setResizing({ id: elId, handle, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y, origW: el.width, origH: el.height });
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (dragging) {
        const el = elements.find(x => x.id === dragging.id);
        if (!el) return;
        const coords = getCanvasCoords(e);
        const nx = Math.max(0, Math.min(100 - el.width, coords.x - dragging.offsetX));
        const ny = Math.max(0, Math.min(100 - el.height, coords.y - dragging.offsetY));
        setElements(prev => prev.map(el => el.id === dragging.id ? { ...el, x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 } : el));
      }
      if (resizing) {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const dx = ((e.clientX - resizing.startX) / rect.width) * 100;
        const dy = ((e.clientY - resizing.startY) / rect.height) * 100;
        const h = resizing.handle;
        let nx = resizing.origX, ny = resizing.origY, nw = resizing.origW, nh = resizing.origH;
        if (h.includes("e")) nw = Math.max(3, resizing.origW + dx);
        if (h.includes("w")) { nw = Math.max(3, resizing.origW - dx); nx = resizing.origX + dx; }
        if (h.includes("s")) nh = Math.max(2, resizing.origH + dy);
        if (h.includes("n")) { nh = Math.max(2, resizing.origH - dy); ny = resizing.origY + dy; }
        setElements(prev => prev.map(el => el.id === resizing.id ? { ...el, x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10, width: Math.round(nw * 10) / 10, height: Math.round(nh * 10) / 10 } : el));
      }
    };
    const handleUp = () => { setDragging(null); setResizing(null); };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => { window.removeEventListener("mousemove", handleMove); window.removeEventListener("mouseup", handleUp); };
  }, [dragging, resizing, elements]);

  const selectedEl = elements.find(e => e.id === selected);

  if (!template) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;

  return (
    <div className="h-screen flex flex-col bg-slate-100" data-testid="template-editor">
      <EditorToolbar
        templateName={template.name}
        selected={selected} selectedEl={selectedEl} history={history}
        saving={saving} savedFeedback={savedFeedback} showPreview={showPreview}
        onBack={() => navigate("/admin/atenciones/plantillas")}
        onSave={save} onUndo={handleUndo}
        onTogglePreview={() => setShowPreview(!showPreview)}
        onToggleLock={toggleLock} onCenterH={centerH}
        onBringToFront={bringToFront} onSendToBack={sendToBack}
        onDuplicate={duplicateSelected} onDelete={deleteSelected}
        onOpenPresets={() => setShowPresets(true)}
        onOpenReset={() => setShowResetConfirm(true)}
      />

      <div className="flex-1 flex overflow-hidden">
        <EditorLeftPanel
          elements={elements} selected={selected}
          bgColor={bgColor} bgImage={bgImage}
          onAddElement={addElement} onUploadCustomImage={uploadCustomImage}
          onSelectElement={setSelected}
          onBgColorChange={(v) => { pushHistory(); setBgColor(v); }}
          onUploadBgImage={uploadBgImage}
          onRemoveBgImage={() => { pushHistory(); setBgImage(""); }}
        />

        {/* CANVAS */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto bg-slate-200/50">
          <div ref={canvasRef} data-canvas="true" data-testid="editor-canvas" className="relative shadow-2xl"
            style={{ width: canvasSize.w, height: canvasSize.h, backgroundColor: bgColor, backgroundImage: bgImage ? `url(${API}${bgImage})` : "none", backgroundSize: "cover", backgroundPosition: "center" }}
            onMouseDown={handleCanvasMouseDown}>
            {!showPreview && (
              <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: "linear-gradient(to right, #475569 1px, transparent 1px), linear-gradient(to bottom, #475569 1px, transparent 1px)", backgroundSize: "10% 10%" }} />
            )}
            {elements.map(el => (
              <CanvasElement key={el.id} el={el} isSelected={selected === el.id && !showPreview}
                onMouseDown={(e) => handleElMouseDown(e, el.id)}
                onResizeMouseDown={(e, handle) => handleResizeMouseDown(e, el.id, handle)}
                preview={showPreview} canvasW={canvasSize.w} />
            ))}
          </div>
        </div>

        {selectedEl && !showPreview && (
          <EditorPropertiesPanel
            selectedEl={selectedEl}
            onUpdateEl={updateEl}
            onToggleLock={toggleLock}
            onDuplicate={duplicateSelected}
            onDelete={deleteSelected}
          />
        )}
      </div>

      <PresetsGallery open={showPresets} onOpenChange={setShowPresets} presets={PRESET_TEMPLATES} onLoadPreset={loadPreset} />
      <ResetConfirmDialog open={showResetConfirm} onOpenChange={setShowResetConfirm} onConfirm={resetToDefault} />
    </div>
  );
}
